/**
 * LogConsultantProposalModal — "Log proposal received" on a Subconsultant candidate or RFP round
 * (TeamSection.jsx). Staff manually record a proposal that came back from the consultant (fee,
 * date received, notes, optional file) — there is no self-service submission from the consultant,
 * per docs/TEAM.md's "internal log only" decision. Ties to an RFP round when one exists (defaults
 * to the newest); rfpId may be omitted if a proposal arrives without a formal request on file.
 *
 * Doubles as the "Edit proposal" modal — pass `existingProposal` to prefill and update in place
 * instead of creating a new row.
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { addConsultantProposal, updateConsultantProposal, uploadConsultantProposalFile } from '../services/consultantRfps.js'

const label = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5, display: 'block' }
const input = { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }

function todayIso() { return new Date().toISOString().slice(0, 10) }

export default function LogConsultantProposalModal({ projectConsultantId, rfps, existingProposal, defaultRfpId, onClose, onAdded }) {
  const isEdit = !!existingProposal
  const [rfpId, setRfpId] = useState(isEdit ? (existingProposal.rfp_id || '') : (defaultRfpId || (rfps && rfps[0] && rfps[0].id) || ''))
  const [fee, setFee] = useState(isEdit && existingProposal.proposed_fee != null ? String(existingProposal.proposed_fee) : '')
  const [dateReceived, setDateReceived] = useState(isEdit && existingProposal.date_received ? String(existingProposal.date_received).slice(0, 10) : todayIso())
  const [notes, setNotes] = useState((isEdit && existingProposal.notes) || '')
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  if (!projectConsultantId) return null

  async function submit() {
    setSaving(true); setError('')
    try {
      let fileUrl = isEdit ? existingProposal.file_url : null
      if (file) fileUrl = await uploadConsultantProposalFile(file)
      if (isEdit) {
        await updateConsultantProposal(existingProposal.id, {
          proposed_fee: fee !== '' ? Number(fee) : null, date_received: dateReceived || null, notes: notes.trim() || null, file_url: fileUrl,
        })
      } else {
        await addConsultantProposal({
          rfpId: rfpId || null, projectConsultantId, proposedFee: fee, dateReceived: dateReceived || null,
          notes: notes.trim() || null, fileUrl,
        })
      }
      onAdded()
    } catch (e) {
      setError((e && e.message) || 'Could not save the proposal.')
      setSaving(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="LogConsultantProposalModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 420, maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>{isEdit ? 'Edit proposal' : 'Log proposal received'}</div>
          <button onClick={onClose} disabled={saving} style={{ background: 'transparent', border: 'none', cursor: saving ? 'default' : 'pointer', color: 'var(--aj-text-muted)', padding: 4, opacity: saving ? 0.5 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!isEdit && rfps && rfps.length > 0 && (
            <div>
              <label style={label}>In response to</label>
              <select style={input} value={rfpId} disabled={saving} onChange={e => setRfpId(e.target.value)}>
                {rfps.map(r => (
                  <option key={r.id} value={r.id}>RFP sent {r.sent_at ? String(r.sent_at).slice(0, 10) : '—'}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label style={label}>Proposed fee</label>
            <input type="number" min="0" step="1" style={input} value={fee} disabled={saving} placeholder="0"
              onChange={e => setFee(e.target.value)} />
          </div>

          <div>
            <label style={label}>Date received</label>
            <input type="date" style={input} value={dateReceived} disabled={saving} onChange={e => setDateReceived(e.target.value)} />
          </div>

          <div>
            <label style={label}>Notes (optional)</label>
            <textarea style={{ ...input, minHeight: 60, resize: 'vertical', fontFamily: '"DM Sans",sans-serif' }} value={notes} disabled={saving}
              onChange={e => setNotes(e.target.value)} />
          </div>

          <div>
            <label style={label}>Proposal file (optional)</label>
            <input type="file" disabled={saving} onChange={e => setFile(e.target.files && e.target.files[0])}
              style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)' }} />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button onClick={onClose} disabled={saving} style={{ ...btnBase, background: 'transparent', color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)' }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...btnBase, background: '#07272D', color: '#fff', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Log proposal'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
